// src/services/firestoreCache.ts
import localforage from 'localforage';

export interface CacheItem<T = any> {
  data: T;
  timestamp: number;
}

export interface CacheOptions {
  cacheDuration?: number;
  storageType?: 'memory' | 'localStorage' | 'sessionStorage' | 'indexedDB';
}

/**
 * Singleton class for managing Firestore data cache across storage types
 */
export class FirestoreCache {
  private static instance: FirestoreCache;
  private memoryCache: Record<string, CacheItem> = {};
  private indexedDB: LocalForage;
  
  private constructor() {
    this.indexedDB = localforage.createInstance({
      name: 'firestoreCache',
      description: 'Cache for Firestore data'
    });
  }
  
  public static getInstance(): FirestoreCache {
    if (!FirestoreCache.instance) {
      FirestoreCache.instance = new FirestoreCache();
    }
    return FirestoreCache.instance;
  }
  
  /**
   * Get a cached item from the specified storage
   */
  async get<T>(key: string, options: CacheOptions = {}): Promise<T | null> {
    const { cacheDuration = 60000, storageType = 'memory' } = options;
    const now = Date.now();
    
    // Try memory cache first (fastest)
    if (this.memoryCache[key] && (now - this.memoryCache[key].timestamp) < cacheDuration) {
      return this.memoryCache[key].data as T;
    }

    // If not in memory, try the specified storage type
    if (storageType === 'localStorage' || storageType === 'sessionStorage') {
      try {
        const storage = window[storageType as 'localStorage' | 'sessionStorage'];
        const cachedItemStr = storage.getItem(`firestore_${key}`);
        
        if (cachedItemStr) {
          const cachedItem = JSON.parse(cachedItemStr) as CacheItem<T>;
          if (now - cachedItem.timestamp < cacheDuration) {
            // Update memory cache for faster access next time
            this.memoryCache[key] = cachedItem;
            return cachedItem.data;
          }
        }
      } catch (err) {
        console.warn(`Error reading from ${storageType}:`, err);
      }
    } else if (storageType === 'indexedDB') {
      try {
        const cached = await this.indexedDB.getItem<CacheItem<T>>(key);
        if (cached && 'timestamp' in cached && 'data' in cached) {
          const cachedItem = cached as CacheItem<T>;
          if (now - cachedItem.timestamp < cacheDuration) {
            // Also update memory cache
            this.memoryCache[key] = cachedItem;
            return cachedItem.data;
          }
        }
      } catch (err) {
        console.warn('Error reading from IndexedDB:', err);
      }
    }
    
    return null;
  }
  
  /**
   * Store an item in the specified cache storage
   */
  async set<T>(key: string, data: T, options: CacheOptions = {}): Promise<void> {
    const { storageType = 'memory' } = options;
    
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now()
    };
    
    // Always update memory cache
    this.memoryCache[key] = cacheItem;
    
    // Store in the specified storage type
    if (storageType === 'localStorage' || storageType === 'sessionStorage') {
      try {
        const storage = window[storageType as 'localStorage' | 'sessionStorage'];
        storage.setItem(`firestore_${key}`, JSON.stringify(cacheItem));
      } catch (err) {
        console.warn(`Error writing to ${storageType}:`, err);
        // If storage is full, clear old items
        if (err instanceof DOMException && err.name === 'QuotaExceededError') {
          this.clearOldItems(storageType as 'localStorage' | 'sessionStorage');
          try {
            const storage = window[storageType as 'localStorage' | 'sessionStorage'];
            storage.setItem(`firestore_${key}`, JSON.stringify(cacheItem));
          } catch (retryErr) {
            console.error(`Failed to write to ${storageType} after clearing:`, retryErr);
          }
        }
      }
    } else if (storageType === 'indexedDB') {
        try {
            // Strip functions and complex objects to make it serializable
            let serializableData;
            
            try {
              // Try to use structured clone (modern browsers)
              serializableData = structuredClone(data);
            } catch (cloneErr) {
              // Fall back to JSON serialization
              serializableData = JSON.parse(JSON.stringify(data));
            }
          
            const serializableCacheItem = {
              data: serializableData,
              timestamp: Date.now()
            };
          
            await this.indexedDB.setItem(key, serializableCacheItem);
          } catch (err) {
            console.warn('Error writing to IndexedDB:', err);
          }
      }
  }
  
  /**
   * Clear old items from storage to free up space
   */
  private clearOldItems(storageType: 'localStorage' | 'sessionStorage'): void {
    const storage = window[storageType];
    const keys = Object.keys(storage).filter(k => k.startsWith('firestore_'));
    
    // Get all cache items with their timestamps
    const items = keys.map(key => {
      try {
        const itemStr = storage.getItem(key);
        if (!itemStr) return { key, timestamp: 0 };
        
        const item = JSON.parse(itemStr) as CacheItem;
        return { key, timestamp: item.timestamp || 0 };
      } catch {
        return { key, timestamp: 0 };
      }
    });
    
    // Sort by age (oldest first) and remove the oldest 20%
    items.sort((a, b) => a.timestamp - b.timestamp);
    const itemsToRemove = items.slice(0, Math.max(1, Math.floor(items.length * 0.2)));
    
    itemsToRemove.forEach(item => {
      storage.removeItem(item.key);
    });
  }
  
  /**
   * Remove an item from all cache storages
   */
  remove(key: string): void {
    // Clear from memory
    delete this.memoryCache[key];
    
    // Clear from localStorage
    try {
      localStorage.removeItem(`firestore_${key}`);
    } catch (err) {
      console.warn('Error removing from localStorage:', err);
    }
    
    // Clear from sessionStorage
    try {
      sessionStorage.removeItem(`firestore_${key}`);
    } catch (err) {
      console.warn('Error removing from sessionStorage:', err);
    }
    
    // Clear from IndexedDB
    try {
      this.indexedDB.removeItem(key).catch(err => {
        console.warn('Error removing from IndexedDB:', err);
      });
    } catch (err) {
      console.warn('Error removing from IndexedDB:', err);
    }
  }
  
  /**
   * Clear all cache data
   */
  clear(): void {
    // Clear memory cache
    this.memoryCache = {};
    
    // Clear localStorage
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith('firestore_'))
        .forEach(key => localStorage.removeItem(key));
    } catch (err) {
      console.warn('Error clearing localStorage:', err);
    }
    
    // Clear sessionStorage
    try {
      Object.keys(sessionStorage)
        .filter(key => key.startsWith('firestore_'))
        .forEach(key => sessionStorage.removeItem(key));
    } catch (err) {
      console.warn('Error clearing sessionStorage:', err);
    }
    
    // Clear IndexedDB
    try {
      this.indexedDB.clear().catch(err => {
        console.warn('Error clearing IndexedDB:', err);
      });
    } catch (err) {
      console.warn('Error clearing IndexedDB:', err);
    }
  }
}